import Holidays from 'date-holidays';

// Initialize US holidays
const hd = new Holidays('US');

/**
 * Check if a given date is a US federal holiday
 */
export const isUSFederalHoliday = (date: Date): boolean => {
    const holidays = hd.getHolidays(date.getFullYear());
    const dateString = date.toISOString().split('T')[0]; // Format: YYYY-MM-DD
    
    return holidays.some(holiday => {
        const holidayDate = new Date(holiday.date);
        const holidayString = holidayDate.toISOString().split('T')[0];
        return holidayString === dateString;
    });
};

/**
 * Check if a given date is a business day (Monday-Friday, excluding US federal holidays)
 * This matches the backend implementation in api/hhl/utils.py
 */
export const isBusinessDay = (date: Date): boolean => {
    const dayOfWeek = date.getDay();
    // Sunday = 0, Saturday = 6
    if (dayOfWeek === 0 || dayOfWeek === 6) {
        return false;
    }
    
    // Check if it's a US federal holiday
    if (isUSFederalHoliday(date)) {
        return false;
    }
    
    return true;
};

/**
 * Get the next business day from the given date (or today if no date provided)
 * Skips weekends and US federal holidays.
 * This matches the backend implementation in api/hhl/utils.py
 */
export const getNextBusinessDay = (startDate?: Date): Date => {
    const date = startDate ? new Date(startDate) : new Date();
    
    // Start checking from the next day
    const nextDay = new Date(date);
    nextDay.setDate(date.getDate() + 1);
    
    // Keep incrementing until we find a business day
    while (!isBusinessDay(nextDay)) {
        nextDay.setDate(nextDay.getDate() + 1);
    }
    
    return nextDay;
};

/**
 * Format a date as YYYY-MM-DD for HTML date input
 */
export const formatDateForInput = (date: Date): string => {
    return date.toISOString().split('T')[0];
};

/**
 * Format a date as MM/DD/YYYY for display
 */
export const formatDateForDisplay = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
    });
};

/**
 * Get a list of upcoming US federal holidays for display/reference
 */
export const getUpcomingHolidays = (monthsAhead: number = 12): Array<{name: string, date: string}> => {
    const currentYear = new Date().getFullYear();
    const nextYear = currentYear + 1;
    
    let allHolidays = [...hd.getHolidays(currentYear), ...hd.getHolidays(nextYear)];
    
    const today = new Date();
    const futureDate = new Date();
    futureDate.setMonth(futureDate.getMonth() + monthsAhead);
    
    return allHolidays
        .filter(holiday => {
            const holidayDate = new Date(holiday.date);
            return holidayDate >= today && holidayDate <= futureDate;
        })
        .map(holiday => ({
            name: holiday.name,
            date: formatDateForDisplay(new Date(holiday.date))
        }))
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
}; 